import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import type { UserRole } from "../../types/types";
import LoadingSpinner from "./LoadingSpinner";
import { useLanguage } from "../../context/LanguageContext";

interface Props {
  element: React.ReactNode;
  requiredRoles?: UserRole[];
  fallback?: React.ReactNode;
}

const UnauthorizedPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <div className="text-center max-w-md">
        <div className="text-6xl font-bold text-[#b2d12e] mb-4">403</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t("protectedRoute.accessDenied")}</h1>
        <p className="text-gray-600 mb-6">{t("protectedRoute.noPermission")}</p>
        <Link to="/" className="inline-block bg-[#b2d12e] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all">
          {t("protectedRoute.goBackHome")}
        </Link>
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ element, requiredRoles = [], fallback }: Props) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (requiredRoles.length && !requiredRoles.includes(user.role)) {
    return fallback ? <>{fallback}</> : <UnauthorizedPage />;
  }

  return <>{element}</>;
};

export default ProtectedRoute;
